import React from 'react';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { fi, enGB } from 'date-fns/locale';
import Select, { MultiValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { InputRange, SearchFilterDropdown } from '../../../common/components';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setSearchFilters } from '../searchBookableAccommodationsSlice';
import { checkRole } from '../../auth/authSlice';
import { LOCALES, ROLES } from '../../../common/constants';
import { useLocale } from '../../../locales/useLocale';

// function BasicSearchFilters() {
const BasicSearchFilters = ({
  selectedDates,
  setSelectedDates,
  searchFilter,
  handleSelectFilterChange,
  handleSearchFilterChange,
  handleRangeFilterChange,
  handleBooleanFilterChange,
}) => {
  const isTravelAgencyOrDomainUser = useAppSelector(
    checkRole([ROLES.TravelAgency, ROLES.TravelAgencyDomainUser]),
  );
  const { t } = useTranslation(['common', 'basicSearchFilters']);
  const dispatch = useAppDispatch();
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return 0; // Return 0 if either date is missing
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate the difference in time
    const differenceInTime = end.getTime() - start.getTime();

    // Convert the time difference to days
    const durationInDays = differenceInTime / (1000 * 60 * 60 * 24);

    return Math.max(durationInDays, 0); // Ensure the duration is not negative
  };

  const formatDateToYYYYMMDD = date => {
    // Ensure the date is a valid Date object
    const validDate = date instanceof Date ? date : new Date(date);

    return validDate
      .toLocaleDateString('fi-FI', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .split('.')
      .reverse()
      .join('-'); // Converts 'dd.MM.yyyy' to 'yyyy-MM-dd'
  };

  const handleDateChange = dates => {
    setSelectedDates(dates);

    if (dates[0]) {
      const formattedDate = formatDateToYYYYMMDD(dates[0]);
      const duration = calculateDuration(
        formattedDate,
        formatDateToYYYYMMDD(dates[1]),
      );

      dispatch(
        setSearchFilters({
          ...searchFilter,
          arrivalDateTime: formattedDate, // Update arrivalDateTime in search filter
          lengthOfStay: duration,
        }),
      );
    }
  };

  type QualityRatingOption = {
    label: string;
    value: number;
  };

  const qualityRatingOptions: QualityRatingOption[] = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
  ];
  const { currentLocale } = useLocale();
  let calLang = fi;
  if (currentLocale === LOCALES.En) {
    calLang = enGB;
  }
  return (
    <>
      <SearchFilterDropdown
        DefaultValue={false}
        Id="accommodations-filter-dates"
        Label={t('basicSearchFilters:filters.dateSelection')}
        MinWidth={760}
        Value={!!(selectedDates[0] && selectedDates[1])}
        ShowCloseButton={false}
      >
        <DatePicker
          locale={calLang}
          selected={selectedDates[0]} // startDate
          onChange={handleDateChange} // Custom handler to update searchFilter
          startDate={selectedDates[0]}
          endDate={selectedDates[1]}
          selectsRange
          inline
          monthsShown={2}
          showWeekNumbers
        />
      </SearchFilterDropdown>
      <SearchFilterDropdown
        DefaultValue={0}
        Id="accommodations-filter-persons"
        Label={t('basicSearchFilters:filters.personAmount')}
        MinWidth={300}
        Value={searchFilter.maxPersonCount ?? 0}
        ShowCloseButton={false}
      >
        <Form.Select
          aria-label={t('basicSearchFilters:filters.personAmount')}
          name="maxPersonCount"
          onChange={handleSelectFilterChange}
          value={searchFilter.maxPersonCount ?? 0}
        >
          <option value="0">
            {t('basicSearchFilters:filters.personAmountDefaultValue')}
          </option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="6">6</option>
          <option value="7">7</option>
          <option value="8">8</option>
          <option value="9">9</option>
          <option value="10">10</option>
          <option value="11">11</option>
          <option value="12">12</option>
          <option value="13">13</option>
          <option value="14">14</option>
          <option value="15">15</option>
          <option value="16">16</option>
          <option value="17">17</option>
          <option value="18">18</option>
          <option value="19">19</option>
          <option value="20">20</option>
        </Form.Select>
      </SearchFilterDropdown>
      <SearchFilterDropdown
        DefaultValue={0}
        Id="accommodations-filter-area"
        Label={t('basicSearchFilters:filters.area')}
        MinWidth={360}
        Value={searchFilter.area ?? 0}
        ShowCloseButton={false}
      >
        <InputRange
          ClassName="mb-0"
          HandleChange={(value: number) =>
            handleRangeFilterChange('area', value)
          }
          Id="accommodations-filter-area-range"
          Name="area"
          Max={1000}
          Min={0}
          Step={10}
          Unit="m2"
          Value={searchFilter.area ?? 0}
        />
      </SearchFilterDropdown>
      {!isTravelAgencyOrDomainUser && (
        <SearchFilterDropdown
          DefaultValue={0}
          Id="accommodations-filter-nearbyradius"
          Label={t('basicSearchFilters:filters.nearbyRadius')}
          MinWidth={360}
          Value={searchFilter.nearbySearchRadius ?? 0}
          ShowCloseButton={false}
        >
          <InputRange
            ClassName="mb-0"
            HandleChange={(value: number) =>
              handleRangeFilterChange('nearbySearchRadius', value)
            }
            Id="accommodations-filter-nearbyradius-range"
            Name="nearbyradius"
            Max={500}
            Min={0}
            Step={10}
            Unit="km"
            Value={searchFilter.nearbySearchRadius ?? 0}
          />
        </SearchFilterDropdown>
      )}
      <SearchFilterDropdown
        DefaultValue={0}
        Id="accommodations-filter-price"
        Label={t('basicSearchFilters:filters.price')}
        MinWidth={360}
        Value={searchFilter.maxPrice ?? 0}
        ShowCloseButton={false}
      >
        <InputRange
          ClassName="mb-0"
          HandleChange={(value: number) =>
            handleRangeFilterChange('maxPrice', value)
          }
          Id="accommodations-filter-price-range"
          Max={10000}
          Min={0}
          Step={10}
          Unit="€"
          Value={searchFilter.maxPrice ?? 0}
        />
      </SearchFilterDropdown>
      <SearchFilterDropdown
        DefaultValue={0}
        Id="accommodations-filter-bedrooms"
        Label={t('basicSearchFilters:filters.bedroomsAndWc')}
        MinWidth={360}
        Value={
          searchFilter.numberOfBedrooms ?? searchFilter.numberOfToilets ?? 0
        }
        ShowCloseButton={false}
      >
        <Form.Group className="mb-16">
          <Form.Label>
            {t('basicSearchFilters:filters.amountOfBedrooms')}
          </Form.Label>
          <Form.Select
            aria-label={t('basicSearchFilters:filters.amountOfBedrooms')}
            name="numberOfBedrooms"
            onChange={handleSelectFilterChange}
            value={searchFilter.numberOfBedrooms ?? 0}
          >
            <option value="0">
              {t('common:inputInstructions.chooseAmount')}
            </option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>{t('basicSearchFilters:filters.amountOfWc')}</Form.Label>
          <Form.Select
            aria-label={t('basicSearchFilters:filters.amountOfWc')}
            name="numberOfToilets"
            onChange={handleSelectFilterChange}
            value={searchFilter.numberOfToilets ?? 0}
          >
            <option value="0">
              {t('common:inputInstructions.chooseAmount')}
            </option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
          </Form.Select>
        </Form.Group>
      </SearchFilterDropdown>
      <SearchFilterDropdown
        DefaultValue={0}
        Id="accommodations-filter-rating"
        Label={t('basicSearchFilters:filters.qualityRating')}
        MinWidth={300}
        Value={searchFilter.qualityRatings?.length ?? 0}
        ShowCloseButton={false}
      >
        <Select
          isMulti
          placeholder={t(
            'basicSearchFilters:filters.qualityRatingDefaultValue',
          )}
          options={qualityRatingOptions}
          value={
            searchFilter.qualityRatings?.map((rating: number) => ({
              label: rating.toString(),
              value: rating,
            })) as MultiValue<QualityRatingOption>
          }
          onChange={selectedOptions => {
            const selectedValues = (
              selectedOptions as MultiValue<QualityRatingOption>
            ).map(option => option.value);
            handleSearchFilterChange('qualityRatings', selectedValues);
          }}
        />
      </SearchFilterDropdown>
      {!isTravelAgencyOrDomainUser && (
        <SearchFilterDropdown
          ShowCloseButton={false}
          DefaultValue={undefined}
          Id="accommodations-filter-interhomeService"
          Label={t('basicSearchFilters:filters.interhomeService')}
          Value={searchFilter.interhomeService ?? undefined}
        >
          <Form.Group>
            <Form.Check
              type="switch"
              id="custom-switch"
              label={t('basicSearchFilters:filters.interhomeService')}
              checked={searchFilter.interhomeService ?? false}
              onChange={e =>
                handleBooleanFilterChange('interhomeService', e.target.checked)
              }
            />
          </Form.Group>
        </SearchFilterDropdown>
      )}
      {!isTravelAgencyOrDomainUser && (
        <SearchFilterDropdown
          ShowCloseButton={false}
          DefaultValue={false}
          Id="accommodations-settings"
          Label={t('basicSearchFilters:filters.settings')}
          Value={
            searchFilter.logPriceCalculation ||
            searchFilter.skipAvailabilityCheck ||
            false
          }
        >
          <Form.Group>
            <Form.Check
              type="switch"
              id="custom-switch-logPriceCalculation"
              label={t(
                'basicSearchFilters:filters.showPriceCalculationSetting',
              )}
              checked={searchFilter.logPriceCalculation ?? false}
              onChange={e =>
                handleBooleanFilterChange(
                  'logPriceCalculation',
                  e.target.checked,
                )
              }
            />
            {!isTravelAgencyOrDomainUser && (
              <Form.Check
                type="switch"
                id="custom-switch-skipAvailabilityCheck"
                label={t(
                  'basicSearchFilters:filters.skipAvailabilityCheckSetting',
                )}
                checked={searchFilter.skipAvailabilityCheck ?? false}
                onChange={e =>
                  handleBooleanFilterChange(
                    'skipAvailabilityCheck',
                    e.target.checked,
                  )
                }
              />
            )}
          </Form.Group>
        </SearchFilterDropdown>
      )}
    </>
  );
};

BasicSearchFilters.propTypes = {
  selectedDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  setSelectedDates: PropTypes.func.isRequired,
  /* eslint-disable react/forbid-prop-types */
  searchFilter: PropTypes.object.isRequired, // The current search filter object
  handleSelectFilterChange: PropTypes.func.isRequired, // Custom handler to update searchFilter
  handleSearchFilterChange: PropTypes.func.isRequired, // Custom handler to update searchFilter
  handleRangeFilterChange: PropTypes.func.isRequired, // Custom handler to update searchFilter
  handleBooleanFilterChange: PropTypes.func.isRequired,
  /* eslint-enable react/forbid-prop-types */
};

export default BasicSearchFilters;
